import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "../../../../components/SnackbarProvider";
import type { CreateWorkflowEntityParams } from "../types";

interface WorkflowEntityDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (params: CreateWorkflowEntityParams) => void;
  isLoading: boolean;
}

/**
 * Dialog for creating an EmailWorkflowProcessedEntity
 * Asks for workflow name and tags (comma separated)
 */
export const WorkflowEntityDialog: React.FC<WorkflowEntityDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const [workflowName, setWorkflowName] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      // Reset state when opening
      setWorkflowName("");
      setTagsInput("");
    }
  }, [open]);

  const handleConfirm = () => {
    if (!workflowName.trim()) {
      showSnackbar("Workflow name is required", 3000, "error");
      return;
    }

    // Parse tags from comma-separated input
    const tags = tagsInput
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    onConfirm({
      workflowName: workflowName.trim(),
      tags,
    });
  };

  const isConfirmDisabled = isLoading || !workflowName.trim();

  // Preview tags
  const previewTags = tagsInput
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Create Workflow Entity
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          disabled={isLoading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create an EmailWorkflowProcessedEntity for this email. The email will
          be stored in S3 first if needed. The entity will be created with
          status NOT_PROCESSED.
        </Typography>

        <TextField
          fullWidth
          label="Workflow Name"
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
          sx={{ mt: 1 }}
          autoFocus
          disabled={isLoading}
          placeholder="e.g., shipmentBackfill, emailAutomation"
          helperText="The workflow that will process this email"
        />

        <TextField
          fullWidth
          label="Tags (comma separated)"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          sx={{ mt: 2 }}
          disabled={isLoading}
          placeholder="e.g., backfill, test, priority"
          helperText="Tags to categorize this email for processing"
        />

        {previewTags.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Tags preview:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {previewTags.map((tag, index) => (
                <Chip key={index} label={tag} size="small" />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        >
          {isLoading ? <CircularProgress size={20} /> : "Create Entity"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
